<template>
  <CRow>
    <CCol sm="12">
      <CCard>
        <CCardHeader class="d-flex flex-wrap align-items-center">
          <div>
            <CIcon name="cil-pencil" />
            <h5 class="d-inline ml-2">{{ pageTitle }}</h5>
          </div>
          <CButton class="ml-auto" color="dark" :to="{ name: 'Manual Invoice List' }">
            <CIcon name="cil-arrow-thick-left" /> Back
          </CButton>
        </CCardHeader>

        <CCardBody>
          <CAlert color="warning">
            Saving creates or updates an editable draft and does not consume an invoice number.
            Use <strong>Finalize</strong> from the list only after checking the recipient, dates and VAT.
          </CAlert>

          <CAlert v-if="validationErrors.length" color="danger">
            <strong>Please correct the following:</strong>
            <ul class="mb-0 mt-2">
              <li v-for="(error, index) in validationErrors" :key="index">{{ error }}</li>
            </ul>
          </CAlert>

          <CForm @submit.prevent="submit">
            <CRow>
              <CCol lg="6">
                <CCard class="h-100">
                  <CCardHeader><strong>Invoice</strong></CCardHeader>
                  <CCardBody>
                    <CSelect
                      label="Billing entity *"
                      :value.sync="form.billing_entity_id"
                      :options="billingEntityOptions"
                      :disabled="loadingEntities"
                    />
                    <CInput
                      type="date"
                      label="Invoice date *"
                      :max="today"
                      :value.sync="form.issue_date"
                    />
                    <CInput
                      type="date"
                      label="Due date"
                      :min="form.issue_date"
                      :value.sync="form.due_date"
                    />
                    <CInput
                      label="Customer reference (optional)"
                      maxlength="30"
                      placeholder="For example: sale company car"
                      :value.sync="form.customer_reference"
                    />
                    <CTextarea
                      label="Invoice note (optional)"
                      rows="4"
                      maxlength="2000"
                      placeholder="Information shown on the invoice"
                      :value.sync="form.invoice_note"
                    />
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol lg="6" class="mt-3 mt-lg-0">
                <CCard class="h-100">
                  <CCardHeader><strong>Recipient</strong></CCardHeader>
                  <CCardBody>
                    <CSelect
                      label="Recipient type *"
                      :value.sync="form.recipient_type"
                      :options="recipientTypeOptions"
                    />
                    <CInput
                      :label="recipientNameLabel"
                      maxlength="45"
                      :value.sync="form.recipient.name"
                    />
                    <CInput
                      v-if="form.recipient_type === 'company'"
                      label="Contact person (optional)"
                      maxlength="100"
                      :value.sync="form.recipient.contact_name"
                    />
                    <CRow>
                      <CCol md="7">
                        <CInput
                          type="email"
                          label="Email *"
                          maxlength="191"
                          :value.sync="form.recipient.email"
                        />
                      </CCol>
                      <CCol md="5">
                        <CInput
                          label="Phone (optional)"
                          maxlength="64"
                          :value.sync="form.recipient.phone"
                        />
                      </CCol>
                    </CRow>
                    <CInput
                      label="Address *"
                      maxlength="191"
                      :value.sync="form.recipient.address"
                    />
                    <CRow>
                      <CCol md="4">
                        <CInput
                          label="Postal code *"
                          maxlength="32"
                          :value.sync="form.recipient.postal_code"
                        />
                      </CCol>
                      <CCol md="8">
                        <CInput
                          label="City *"
                          maxlength="128"
                          :value.sync="form.recipient.city"
                        />
                      </CCol>
                    </CRow>
                    <CSelect
                      label="Country *"
                      :value.sync="form.recipient.country_id"
                      :options="countryOptions"
                    />
                    <template v-if="form.recipient_type === 'company'">
                      <CInput
                        label="VAT number (optional)"
                        maxlength="20"
                        placeholder="BE0123456789"
                        :value.sync="form.recipient.vat_number"
                      />
                      <CInput
                        label="Peppol participant ID (optional)"
                        maxlength="191"
                        placeholder="For example: 0208:0123456789"
                        :value.sync="form.recipient.peppol_participant_id"
                      />
                      <CAlert v-if="isBelgianCompany" color="info" class="mb-0">
                        For a Belgian VAT-registered company or restaurant, emailing the PDF/UBL does
                        not replace the configured Peppol/accounting transfer.
                      </CAlert>
                    </template>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            <CCard class="mt-3">
              <CCardHeader class="d-flex align-items-center">
                <strong>Invoice lines</strong>
                <CButton class="ml-auto" color="info" size="sm" variant="outline" @click="addLine">
                  <CIcon name="cil-plus" /> Add line
                </CButton>
              </CCardHeader>
              <CCardBody>
                <datalist id="manual-invoice-vat-rates">
                  <option value="0" />
                  <option value="6" />
                  <option value="12" />
                  <option value="21" />
                </datalist>

                <div
                  v-for="(line, index) in form.lines"
                  :key="line.key"
                  class="invoice-line"
                >
                  <div class="d-flex align-items-center mb-2">
                    <strong>Line {{ index + 1 }}</strong>
                    <CButton
                      class="ml-auto"
                      color="danger"
                      size="sm"
                      shape="pill"
                      variant="ghost"
                      :disabled="form.lines.length === 1"
                      @click="removeLine(index)"
                    >
                      <CIcon name="cil-trash" /> Remove
                    </CButton>
                  </div>

                  <CTextarea
                    label="Description *"
                    rows="2"
                    maxlength="1000"
                    placeholder="For example: used company vehicle, chassis number ..."
                    :value.sync="line.description"
                  />

                  <CRow>
                    <CCol sm="6" lg="2">
                      <CInput
                        type="number"
                        min="0.001"
                        step="0.001"
                        label="Quantity *"
                        :value.sync="line.quantity"
                      />
                    </CCol>
                    <CCol sm="6" lg="2">
                      <CInput
                        label="Unit code"
                        maxlength="8"
                        placeholder="EA"
                        :value.sync="line.unit_code"
                      />
                    </CCol>
                    <CCol sm="6" lg="2">
                      <CInput
                        type="number"
                        min="0"
                        step="0.0001"
                        label="Unit price excl. VAT *"
                        :value.sync="line.unit_price_excl"
                      >
                        <template #prepend-content>€</template>
                      </CInput>
                    </CCol>
                    <CCol sm="6" lg="2">
                      <label :for="`vat-rate-${line.key}`">VAT % *</label>
                      <input
                        :id="`vat-rate-${line.key}`"
                        v-model="line.vat_rate"
                        class="form-control"
                        type="number"
                        list="manual-invoice-vat-rates"
                        min="0"
                        max="100"
                        step="0.01"
                      />
                      <small class="form-text text-muted">Choose or enter a percentage.</small>
                    </CCol>
                    <CCol sm="6" lg="2">
                      <CInput
                        label="Ledger code"
                        maxlength="32"
                        :value.sync="line.accounting_cost_code"
                      />
                    </CCol>
                    <CCol sm="6" lg="2">
                      <label>Line total incl.</label>
                      <div class="line-total">{{ money(lineTotalIncl(line)) }}</div>
                    </CCol>
                  </CRow>

                  <CRow v-if="Number(line.vat_rate) === 0">
                    <CCol md="4">
                      <CSelect
                        label="0% VAT category *"
                        :value.sync="line.tax_category"
                        :options="zeroVatCategoryOptions"
                      />
                    </CCol>
                    <CCol md="8" v-if="requiresExemptionReason(line)">
                      <CInput
                        label="VAT exemption reason *"
                        maxlength="500"
                        placeholder="Legal or factual reason for the exemption"
                        :value.sync="line.tax_exemption_reason"
                      />
                    </CCol>
                  </CRow>
                </div>
              </CCardBody>
            </CCard>

            <CRow class="mt-3">
              <CCol lg="7">
                <CCard>
                  <CCardHeader><strong>VAT breakdown</strong></CCardHeader>
                  <CCardBody class="p-0">
                    <table class="table table-striped mb-0">
                      <thead>
                        <tr>
                          <th>VAT rate</th>
                          <th class="text-right">Excl. VAT</th>
                          <th class="text-right">VAT</th>
                          <th class="text-right">Incl. VAT</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in vatBreakdown" :key="row.rate">
                          <td>{{ formatRate(row.rate) }}%</td>
                          <td class="text-right">{{ money(row.excl) }}</td>
                          <td class="text-right">{{ money(row.vat) }}</td>
                          <td class="text-right">{{ money(row.incl) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol lg="5">
                <CCard color="light" class="invoice-totals">
                  <CCardBody>
                    <div class="d-flex justify-content-between">
                      <span>Total excl. VAT</span><strong>{{ money(totals.excl) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mt-2">
                      <span>VAT</span><strong>{{ money(totals.vat) }}</strong>
                    </div>
                    <hr />
                    <div class="d-flex justify-content-between total-incl">
                      <span>Total incl. VAT</span><strong>{{ money(totals.incl) }}</strong>
                    </div>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </CForm>
        </CCardBody>

        <CCardFooter class="sticky-bottom d-flex flex-wrap align-items-center">
          <span class="text-muted mr-auto">A draft cannot be emailed, transferred or paid.</span>
          <CButton color="secondary" :to="{ name: 'Manual Invoice List' }">Cancel</CButton>
          <CButton class="ml-2" color="primary" @click="submit" :disabled="saving || loadingEntities || loadingInvoice">
            <CSpinner v-if="saving" size="sm" />
            <CIcon v-else name="cil-check" /> {{ isEditing ? 'Save draft' : 'Create draft' }}
          </CButton>
        </CCardFooter>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
const localDate = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}

const addDays = (value, days) => {
  const date = new Date(`${value}T12:00:00`)
  date.setDate(date.getDate() + days)
  return localDate(date)
}

let lineKey = 0
const emptyLine = () => ({
  key: ++lineKey,
  description: '',
  quantity: 1,
  unit_code: 'EA',
  unit_price_excl: '',
  vat_rate: 21,
  tax_category: 'Z',
  tax_exemption_reason: '',
  accounting_cost_code: '700300',
})

export default {
  name: 'CreateManualInvoice',
  data() {
    const issueDate = localDate()
    return {
      today: issueDate,
      billingEntities: [],
      loadingEntities: false,
      loadingInvoice: false,
      saving: false,
      validationErrors: [],
      form: {
        billing_entity_id: '',
        recipient_type: 'individual',
        recipient: {
          name: '',
          contact_name: '',
          email: '',
          phone: '',
          address: '',
          postal_code: '',
          city: '',
          country_id: this.$store.state.countryId || 1,
          vat_number: '',
          peppol_participant_id: '',
        },
        customer_reference: '',
        issue_date: issueDate,
        due_date: addDays(issueDate, 14),
        invoice_note: '',
        lines: [emptyLine()],
      },
      recipientTypeOptions: [
        { value: 'individual', label: 'Private individual' },
        { value: 'company', label: 'Company / restaurant' },
      ],
      zeroVatCategoryOptions: [
        { value: 'Z', label: 'Z — Zero rated' },
        { value: 'E', label: 'E — Exempt from VAT' },
        { value: 'O', label: 'O — Outside scope of VAT' },
        { value: 'K', label: 'K — Intra-community supply' },
        { value: 'G', label: 'G — Export outside the EU' },
        { value: 'IC', label: 'IC — Reverse charge' },
      ],
    }
  },
  computed: {
    itemId() {
      return Number(this.$route.params.id) || null
    },
    isEditing() {
      return this.itemId !== null
    },
    pageTitle() {
      return this.isEditing ? 'Edit Manual Invoice Draft' : 'Create Manual Invoice Draft'
    },
    billingEntityOptions() {
      return [{ value: '', label: 'Select a billing entity' }].concat(
        this.billingEntities.map(entity => ({
          value: entity.id,
          label: `${entity.code} — ${entity.legal_name} (${entity.country_code})`,
        }))
      )
    },
    countryOptions() {
      return this.authCountries.map(country => ({ value: country.value, label: country.label }))
    },
    recipientNameLabel() {
      return this.form.recipient_type === 'company' ? 'Company / restaurant name *' : 'Full name *'
    },
    isBelgianCompany() {
      return this.form.recipient_type === 'company'
        && Number(this.form.recipient.country_id) === 1
        && Boolean(String(this.form.recipient.vat_number || '').trim())
    },
    vatBreakdown() {
      const breakdown = {}
      this.form.lines.forEach(line => {
        const rate = this.validNumber(line.vat_rate)
        const key = String(rate)
        if (!breakdown[key]) breakdown[key] = { rate, excl: 0, vat: 0, incl: 0 }
        const excl = this.lineTotalExcl(line)
        const vat = this.roundMoney(excl * rate / 100)
        breakdown[key].excl = this.roundMoney(breakdown[key].excl + excl)
        breakdown[key].vat = this.roundMoney(breakdown[key].vat + vat)
        breakdown[key].incl = this.roundMoney(breakdown[key].incl + excl + vat)
      })
      return Object.values(breakdown).sort((left, right) => left.rate - right.rate)
    },
    totals() {
      return this.vatBreakdown.reduce((totals, row) => ({
        excl: this.roundMoney(totals.excl + row.excl),
        vat: this.roundMoney(totals.vat + row.vat),
        incl: this.roundMoney(totals.incl + row.incl),
      }), { excl: 0, vat: 0, incl: 0 })
    },
  },
  async mounted() {
    await this.loadBillingEntities()
    if (this.isEditing) await this.loadInvoice()
  },
  methods: {
    async loadBillingEntities() {
      this.loadingEntities = true
      try {
        const response = await this.$axios.get(this.$backend.BILLING_ENTITIES.OPTIONS)
        this.billingEntities = (response.data || []).filter(entity => entity.is_enabled !== false)
        const countryCode = this.selectedCountryCode()
        const active = this.billingEntities.find(entity => (
          entity.country_code === countryCode && entity.status === 'active'
        ))
        const fallback = this.billingEntities.find(entity => entity.status === 'active')
          || this.billingEntities[0]
        if (!this.isEditing) {
          this.form.billing_entity_id = active ? active.id : (fallback ? fallback.id : '')
        }
      } catch (error) {
        this.showError(error, 'Billing entities could not be loaded.')
      } finally {
        this.loadingEntities = false
      }
    },
    async loadInvoice() {
      this.loadingInvoice = true
      try {
        const url = this.$backend.MANUAL_INVOICES.SHOW.replace('{id}', this.itemId)
        const response = await this.$axios.get(url)
        const invoice = response.data
        if (!invoice.is_editable) {
          this.$toast.error('Only draft manual invoices can be edited.')
          await this.$router.replace({ name: 'Manual Invoice List' })
          return
        }
        this.form = {
          billing_entity_id: invoice.billing_entity_id,
          recipient_type: invoice.recipient_type,
          recipient: {
            name: invoice.ivName || '',
            contact_name: invoice.ivPname === invoice.ivName ? '' : (invoice.ivPname || ''),
            email: invoice.recipient_email || '',
            phone: invoice.recipient_phone || '',
            address: invoice.ivAddress || '',
            postal_code: invoice.ivPostcode || '',
            city: invoice.ivCity || '',
            country_id: invoice.ivCountryId || 1,
            vat_number: invoice.ivVat || '',
            peppol_participant_id: invoice.peppol_participant_id || '',
          },
          customer_reference: invoice.ivCustomer || '',
          issue_date: this.formatApiDate(invoice.ivDate),
          due_date: this.formatApiDate(invoice.payment_due_date),
          invoice_note: invoice.invoice_note || '',
          lines: (invoice.lines || []).map(line => ({
            key: ++lineKey,
            description: line.description,
            quantity: line.quantity,
            unit_code: line.unit_code,
            unit_price_excl: line.unit_price_excl,
            vat_rate: line.vat_rate,
            tax_category: line.tax_category || 'Z',
            tax_exemption_reason: line.tax_exemption_reason || '',
            accounting_cost_code: line.accounting_cost_code || '700300',
          })),
        }
        if (!this.form.lines.length) this.form.lines = [emptyLine()]
      } catch (error) {
        this.showError(error, 'The draft could not be loaded.')
      } finally {
        this.loadingInvoice = false
      }
    },
    selectedCountryCode() {
      const country = this.authCountries.find(item => (
        Number(item.value) === Number(this.form.recipient.country_id)
      ))
      return country && country.iso2 ? country.iso2.toUpperCase() : null
    },
    addLine() {
      if (this.form.lines.length < 100) this.form.lines.push(emptyLine())
    },
    removeLine(index) {
      if (this.form.lines.length > 1) this.form.lines.splice(index, 1)
    },
    requiresExemptionReason(line) {
      return Number(line.vat_rate) === 0
        && ['E', 'O', 'K', 'G', 'IC'].includes(line.tax_category)
    },
    lineTotalExcl(line) {
      return this.roundMoney(this.validNumber(line.quantity) * this.validNumber(line.unit_price_excl))
    },
    lineTotalIncl(line) {
      const excl = this.lineTotalExcl(line)
      return this.roundMoney(excl + (excl * this.validNumber(line.vat_rate) / 100))
    },
    validNumber(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    roundMoney(value) {
      return Math.round((Number(value) + Number.EPSILON) * 100) / 100
    },
    validate() {
      const errors = []
      const recipient = this.form.recipient
      if (!this.form.billing_entity_id) errors.push('Select a billing entity.')
      if (!this.form.issue_date) errors.push('Enter an invoice date.')
      if (this.form.issue_date > this.today) errors.push('The invoice date cannot be in the future.')
      if (this.form.due_date && this.form.due_date < this.form.issue_date) {
        errors.push('The due date cannot be before the invoice date.')
      }
      if (!String(recipient.name || '').trim()) errors.push('Enter the recipient name.')
      if (!/^\S+@\S+\.\S+$/.test(String(recipient.email || '').trim())) {
        errors.push('Enter a valid recipient email address.')
      }
      if (!String(recipient.address || '').trim()) errors.push('Enter the recipient address.')
      if (!String(recipient.postal_code || '').trim()) errors.push('Enter the recipient postal code.')
      if (!String(recipient.city || '').trim()) errors.push('Enter the recipient city.')
      if (!recipient.country_id) errors.push('Select the recipient country.')

      this.form.lines.forEach((line, index) => {
        const prefix = `Line ${index + 1}:`
        if (!String(line.description || '').trim()) errors.push(`${prefix} enter a description.`)
        if (!(Number(line.quantity) > 0)) errors.push(`${prefix} quantity must be greater than zero.`)
        if (line.unit_price_excl === '' || Number(line.unit_price_excl) < 0) {
          errors.push(`${prefix} enter a valid unit price excluding VAT.`)
        }
        if (line.vat_rate === '' || Number(line.vat_rate) < 0 || Number(line.vat_rate) > 100) {
          errors.push(`${prefix} VAT must be between 0% and 100%.`)
        }
        if (Number(line.vat_rate) === 0 && !line.tax_category) {
          errors.push(`${prefix} select a 0% VAT category.`)
        }
        if (this.requiresExemptionReason(line) && !String(line.tax_exemption_reason || '').trim()) {
          errors.push(`${prefix} enter the VAT exemption reason.`)
        }
      })
      this.validationErrors = errors
      return errors.length === 0
    },
    payload() {
      const clean = value => {
        const result = String(value || '').trim()
        return result || null
      }
      return {
        billing_entity_id: Number(this.form.billing_entity_id),
        recipient_type: this.form.recipient_type,
        recipient: {
          name: String(this.form.recipient.name).trim(),
          contact_name: clean(this.form.recipient.contact_name),
          email: String(this.form.recipient.email).trim(),
          phone: clean(this.form.recipient.phone),
          address: String(this.form.recipient.address).trim(),
          postal_code: String(this.form.recipient.postal_code).trim(),
          city: String(this.form.recipient.city).trim(),
          country_id: Number(this.form.recipient.country_id),
          vat_number: this.form.recipient_type === 'company'
            ? clean(this.form.recipient.vat_number) : null,
          peppol_participant_id: this.form.recipient_type === 'company'
            ? clean(this.form.recipient.peppol_participant_id) : null,
        },
        customer_reference: clean(this.form.customer_reference),
        issue_date: this.form.issue_date,
        due_date: this.form.due_date || null,
        invoice_note: clean(this.form.invoice_note),
        lines: this.form.lines.map(line => ({
          description: String(line.description).trim(),
          quantity: Number(line.quantity),
          unit_code: String(line.unit_code || 'EA').trim().toUpperCase(),
          unit_price_excl: Number(line.unit_price_excl),
          vat_rate: Number(line.vat_rate),
          tax_category: Number(line.vat_rate) > 0 ? 'S' : line.tax_category,
          tax_exemption_reason: Number(line.vat_rate) === 0
            ? clean(line.tax_exemption_reason) : null,
          accounting_cost_code: clean(line.accounting_cost_code),
        })),
      }
    },
    async submit() {
      if (!this.validate()) {
        window.scrollTo(0, 0)
        return
      }
      const confirmation = await this.mSwal.fire({
        title: this.isEditing ? 'Save draft changes?' : 'Create this draft?',
        html: `The editable draft total is <strong>${this.money(this.totals.incl)}</strong>. No invoice number will be consumed.`,
        icon: 'question',
        confirmButtonText: this.isEditing ? 'Save draft' : 'Create draft',
      })
      if (!confirmation.isConfirmed) return

      this.saving = true
      this.validationErrors = []
      try {
        const response = this.isEditing
          ? await this.$axios.put(
            this.$backend.MANUAL_INVOICES.UPDATE.replace('{id}', this.itemId),
            this.payload()
          )
          : await this.$axios.post(this.$backend.MANUAL_INVOICES.STORE, this.payload())
        const invoice = response.data
        await this.mSwal.fire({
          title: `Draft #${invoice.id} saved`,
          text: 'Review it in the list and finalize it when all details are correct.',
          icon: 'success',
          showCancelButton: false,
          confirmButtonText: 'Go to manual invoices',
        })
        await this.$router.push({ name: 'Manual Invoice List' })
      } catch (error) {
        this.validationErrors = this.errorMessages(error, 'The invoice could not be created.')
        window.scrollTo(0, 0)
      } finally {
        this.saving = false
      }
    },
    errorMessages(error, fallback) {
      const response = error && error.response && error.response.data
      if (response && response.errors) {
        return Object.values(response.errors).reduce((all, values) => all.concat(values), [])
      }
      return [(response && response.message) || fallback]
    },
    showError(error, fallback) {
      this.$toast.error(this.errorMessages(error, fallback).join(' '))
    },
    formatApiDate(value) {
      return value ? String(value).slice(0, 10).replace(/\//g, '-') : ''
    },
    formatRate(value) {
      return Number(value || 0).toLocaleString('nl-BE', { maximumFractionDigits: 2 })
    },
    money(value) {
      return Number(value || 0).toLocaleString('nl-BE', {
        style: 'currency',
        currency: 'EUR',
      })
    },
  },
}
</script>

<style scoped>
.invoice-line {
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  padding: 1rem;
}
.invoice-line + .invoice-line {
  margin-top: 1rem;
}
.line-total {
  min-height: 38px;
  border-radius: 0.25rem;
  background: #ebedef;
  padding: 0.55rem 0.75rem;
  font-weight: 700;
}
.invoice-totals .total-incl {
  font-size: 1.25rem;
}
</style>
